/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";

import * as yup from "yup";

import { Form as FormikForm, Formik, FormikConfig, useFormikContext } from "formik";

import { Button, Grid } from "@mui/material";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { SelectOrganization } from "components/haven-design/form/special-fields";

import { AdminDetailedListUsersQuery_adminApplicants as AdminApplicant, PostgresUserRole } from "types/gql-types";
import { SwitchWithErrorLabel } from "components/haven-design/form/checkbox";
import { validatePassword, PasswordValidationType } from "components/login/utils";
import { PasswordValidations } from "components/login/components/shared-components";
import RolesInput from "./components/roles-input";

const userSchema = yup.object({
  user: yup.object({
    loginEmail: yup.string().nullable(),
    loginPhone: yup.string().nullable(),
    emailVerified: yup.boolean().nullable(),
    phoneVerified: yup.boolean().nullable(),
    password: yup.string().optional(),
    organizationId: yup.string().nullable(),
  }).required(),
  roles: yup.array(yup.string().required().oneOf(Object.values(PostgresUserRole))).required(),
  profile: yup.object({
    firstName: yup.string().required().default(""),
    lastName: yup.string().required().default(""),
  }).required(),
});

type FormikUser = yup.InferType<typeof userSchema>;

const defaultUser: FormikUser = {
  ...userSchema.getDefault(),
  roles: [PostgresUserRole.PROPERTY_MANAGER, PostgresUserRole.SUPERVISOR],
};

const userToFormik = (
  {
    loginEmail,
    loginPhone,
    emailVerified,
    phoneVerified,
    profile: { firstName, lastName },
    organization,
    userRoles,
  }: AdminApplicant,
): FormikUser => ({
  user: {
    loginEmail,
    loginPhone,
    emailVerified,
    phoneVerified,
    organizationId: organization?.id,
    // eslint-disable-next-line no-undefined
    password: undefined,
  },
  profile: {
    firstName: firstName || "",
    lastName: lastName || "",
  },
  roles: userRoles.map(role => role.role),
});

const PasswordValidationsFormik = () => {
  const formik = useFormikContext<FormikUser>();
  const [errors, setErrors] = useState<PasswordValidationType[]>([]);
  useEffect(() => {
    setErrors(validatePassword(formik.values.user.password || ""));
  }, [formik.values.user.password]);
  return <PasswordValidations errors={errors} />;
};

export type FormProps = {
  user?: AdminApplicant
  onSubmit: FormikConfig<FormikUser>["onSubmit"]
}

const Form: React.FC<FormProps> = ({ user, onSubmit }) => {
  const formikUser = user ? userToFormik(user) : defaultUser;

  return (
    <Formik
      initialValues={formikUser}
      validationSchema={userSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      <Grid container spacing={2}>
        <FormikForm autoComplete="off">
          <input type="hidden" autoComplete="false"/>
          <Grid container spacing={2} size={{ xs: 12, sm: 8 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Input margin="none" name="profile.firstName" type="text" label="Profile First Name" />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Input margin="none" name="profile.lastName" type="text" label="Profile Last Name" />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Input margin="none" name="user.loginEmail" type="email" label="Login Email" />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <SwitchWithErrorLabel name="user.emailVerified" label="Email is Verified?" />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Input margin="none" name="user.loginPhone" type="tel" label="Login Phone" />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <SwitchWithErrorLabel name="user.phoneVerified" label="Phone is Verified?" />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Input margin="none"
                name="user.password"
                type="password"
                label="Password"
                helperText={user?.id ? "Leave this blank to not change their password." : "Required for all accounts with an email"}
                slotProps={{ htmlInput: { autocomplete: "false" } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <PasswordValidationsFormik />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <SelectOrganization name="user.organizationId" label="Organization" />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <RolesInput />
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
            >
              {user?.id ? "Update User" : "Create User"}
            </Button>
          </Grid>
        </FormikForm>
      </Grid>
    </Formik>
  );
};

export default Form;
